/**
 * WIT-FIX-PWA-WELDERCERT-001 — the welder's OWN certificate picker (Erik,
 * 09.10.2026: «a normal user sees only their own certificates, and only those
 * that can apply to the weld in front of them»), as pure presenters over what
 * the server sent, so node --test can pin them (tests/js/cert-own-picker.test.js).
 *
 * The server is the boundary: the joints payload's `certificates` is already
 * the welder's own list, each joint's `cert_candidates` names the ones the
 * matcher did not rule out for that weld, and certificate-matches answers
 * with the verdict and reason per certificate (not_qualified left out). This
 * only draws that answer — it never widens a list.
 */
const ORDER = ['qualified', 'cannot_verify', 'not_qualified']

/**
 * The ids the server still allows on this weld: the certificate-matches
 * answer when it is loaded, else the joint's `cert_candidates`, else null
 * (not judged — matching off or an older server: the plain own list).
 */
export function relevantIds ({ matches, candidateIds }) {
  if (matches) {
    return Object.keys(matches).filter(id => matches[id] && matches[id].verdict !== 'not_qualified')
  }
  if (Array.isArray(candidateIds)) return candidateIds.map(String)
  return null
}

/** «Velges automatisk · n aktuelle» — n. */
export function ownRelevantCount ({ matches, candidateIds, certificates }) {
  const r = relevantIds({ matches, candidateIds })
  return r ? r.length : (certificates || []).length
}

/**
 * The rows of the own picker: own certificates the server allows here (plus
 * what is already stored on the weld, so a saved choice never disappears),
 * each with the verdict, the one-line reason and the short label. Qualified
 * first, then cannot_verify; within a group the longest validity, then the
 * lowest id — the order «Ferdig sveist» picks in (D-PWACERT-3).
 */
export function ownPickerRows ({ certificates, matches, candidateIds, keepIds, verdictText }) {
  const keep = (keepIds || []).map(String)
  const relevant = relevantIds({ matches, candidateIds })
  const allowed = relevant ? new Set(relevant.concat(keep)) : null
  const text = verdictText || {}
  const rows = (certificates || [])
    .filter(c => !allowed || allowed.has(String(c.value)))
    .map(c => {
      const id = String(c.value)
      const hit = matches ? matches[id] : null
      const verdict = hit && ORDER.includes(hit.verdict) ? hit.verdict : null
      const label = c.shortLabel || c.label || id
      return {
        value: id,
        label,
        summary: (hit && hit.summary) || c.summary || '',
        verdict,
        verdictText: verdict ? (text[verdict] || verdict) : '',
        why: (hit && (hit.why || (hit.reasons || []).join('; '))) || '',
        kept: keep.includes(id),
        relevant: !relevant || relevant.includes(id),
        endDate: String(c.endDate || '')
      }
    })
    .filter(r => r.verdict !== 'not_qualified' || r.kept)
  return rows.sort((a, b) =>
    (ORDER.indexOf(a.verdict) - ORDER.indexOf(b.verdict)) ||
    (a.endDate < b.endDate ? 1 : a.endDate > b.endDate ? -1 : 0) ||
    (Number(a.value) - Number(b.value)))
}

/**
 * What the empty picker says. The server's own sentence wins (it carries the
 * weld number); the keys are the fallbacks for an older server.
 *   no_certificates → welder_id_missing (no competence record at all, P5)
 *   none_relevant   → unqualified_row (nothing of theirs applies here)
 */
export function ownEmptyKey ({ ownState, matchesState }) {
  if (ownState === 'no_certificates' || matchesState === 'no_certificates') return 'welder_id_missing'
  return 'unqualified_row'
}
